import {
  Button,
  Card,
  Conditional,
  Select,
  Table,
} from "@result/rui/components";
import { useForm, useNotifications } from "@result/rui/hooks";
import { ApiFilter, ApiPage, ApiSort } from "@result/rui/utils/api";
import { toHumanReadableDate } from "@result/rui/utils/date";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useParams } from "react-router";
import * as yup from "yup";

import PageHeader from "#/components/PageHeader.jsx";
import SelectLookupTableItem from "#/components/SelectLookupTableItem.jsx";
import DataLayerSearchSelect from "#/components/data-layer/SearchSelect.jsx";
import PrintContent from "#/components/pdf/PrintContent";
import * as DLD from "#/def/data-layer.js";
import { PLEMENSKA_VREDNOST_KODA } from "#/def/data-layer.js";
import * as LTD from "#/def/lookup-tables.ident.gen.js";
import useCallbackOnMatch from "#/hooks/useCallbackOnMatch";
import { useDataLayerQuery, useDataLayerRead } from "#/hooks/useDataLayer.js";
import { useLookupTableList } from "#/hooks/useLookupTables.js";
import { filterLastnostiPvBySpecies } from "#/pages/breeding-values/BreedingValueUtils";
import BreedingValuesMetodaObracunaCheckbox from "#/pages/breeding-values/BreedingValuesMetodaObracunaCheckbox";
import BreedingValuesTipPvCheckbox from "#/pages/breeding-values/BreedingValuesTipPvCheckbox";
import KodaIzracunSelect from "#/pages/breeding-values/KodaIzracunSelect.jsx";
import { useBreedingValuesKodaIzracunQuery } from "#/pages/breeding-values/useBreedingValuesKodaIzracunQuery";
import { getRangPvForAnimal } from "#/redux/resource/RangPvSlice";
import * as species from "#/utils/SpeciesScope";

const BreedingValuesCatalogPage = ({ showHeader = true, plemenskaZivalId }) => {
  const { t } = useTranslation();
  const { animalId } = useParams();
  const [wasInitialFetchTriggered, setWasInitialFetchTriggered] =
    useState(false);
  const [initialValues, setInitialValues] = useState({});
  const [plemenskaZival, setPlemenskaZival] = useState(null);
  const [zival, setZival] = useState(null);
  const { addErrorNotification } = useNotifications();
  const [rangForAnimal, setRangForAnimal] = useState({});
  const dispatch = useDispatch();

  // TODO: plemenska zival is just a snapshot of animal. Single animal can appear as multiple entries in plemenskaZival and with multiple different eksterniEnolicniIdentifikator. Adjust fetch logic according to that...
  const pv = useDataLayerQuery(DLD.PLEMENSKE_VREDNOSTI);

  const initialAnimalPv = useDataLayerQuery(DLD.PLEMENSKE_VREDNOSTI);
  const [initialAnimalKodaIzracun, setInitialAnimalKodaIzracun] =
    useState(undefined);

  const kodaIzracunQuery = useBreedingValuesKodaIzracunQuery();

  const searchBreedingValues = (values) => {
    const filter = species.filter();

    filter.equal(
      "plemenskaZival.eksterniEnolicniIdentifikator",
      plemenskaZival?.eksterniEnolicniIdentifikator
    );

    filter.or((orGroup) => {
      values.verzijeKataloga.forEach((verzija) => {
        orGroup.equal("kodaIzracun", verzija);
      });
    });

    filter.or((orGroup) => {
      values.tipiPv.forEach((tip) => {
        orGroup.equal("tipPlemenskeVrednosti", tip);
      });
    });

    filter.or((orGroup) => {
      values.metodeIzracuna.forEach((metoda) => {
        orGroup.equal("metodaIzracuna", metoda);
      });
    });

    if (values.kodaPopulacije != null && values.kodaPopulacije !== "VSE") {
      filter.equal("kodaPopulacije", values.kodaPopulacije);
    }

    if (values.lastnost != null) {
      filter.equal("kodaPv", values.lastnost);
    }

    pv.query({
      page: new ApiPage().size(10000),
      filter,
      sort: new ApiSort().sort("tipPlemenskeVrednosti", "DESC"),
    }).catch((e) => {
      console.log("Error fetching breeding values", e);
      addErrorNotification(t("breedingValues.errorFetching"));
    });
  };

  const validationSchema = {
    zivalId: yup.string().required(),
    verzijeKataloga: yup.array().of(yup.string()).min(1).required(),
  };

  const { getField, submit, values, setValues } = useForm({
    initialValues: {
      metodeIzracuna: [],
      tipiPv: [],
      verzijeKataloga: [],
      zivalId: null,
      kodaPopulacije: "VSE",
    },
    schema: validationSchema,
    onSubmit: searchBreedingValues,
  });

  const tipiPv = useLookupTableList(LTD.SifPlemVredTipPv);
  const kodePopulacij = useLookupTableList(LTD.SifPlemVredKodaPopulacije);

  useEffect(() => {
    if (plemenskaZivalId != null) {
      plemenskaZivalRead.read(plemenskaZivalId).then((pz) => {
        getField("zivalId").setValue(pz.eksterniEnolicniIdentifikator);
      });
      getInitialAnimalKodaIzracun(plemenskaZivalId);
    }
  }, [plemenskaZivalId]);

  useEffect(() => {
    if (tipiPv != null && tipiPv.length > 0) {
      getField("tipiPv").setValue(tipiPv.map((t) => t.sifra));
    }
  }, [tipiPv?.length]);

  const breedingValueCodeQuery = useDataLayerQuery({
    ident: PLEMENSKA_VREDNOST_KODA,
  });

  const plemenskaZivalRead = useDataLayerRead(DLD.PLEMENSKA_ZIVAL);
  const plemenskaZivalQuery = useDataLayerQuery(DLD.PLEMENSKA_ZIVAL);
  const zivalIdentifikatorQuery = useDataLayerQuery(DLD.ZIVAL_IDENTIFIKATOR);
  const zivalAggregateRead = useDataLayerRead(DLD.ZIVAL_AGGREGATE);

  const [breedingValueCodes, setBreedingValueCodes] = useState([]);

  const distinctSklopi = useMemo(() => {
    return Array.from(new Set(breedingValueCodes.map((bv) => bv.sklop))).filter(
      Boolean
    );
  }, [breedingValueCodes]);

  const breedingValuesLists = useMemo(() => {
    const sklop = values.sklop;
    if (breedingValueCodeQuery.result?.length === 0) {
      return [];
    }
    let pvResults = pv.result?.content ?? [];
    pvResults = pvResults.map((breedingValue) => {
      return {
        ...breedingValue,
        lastnost: breedingValueCodes.find(
          (code) => code.sifra === breedingValue.kodaPv
        ),
      };
    });
    pvResults = pvResults.sort((a, b) => {
      const sklopA = a.lastnost?.sklop?.toUpperCase() ?? "";
      const sklopB = b.lastnost?.sklop?.toUpperCase() ?? "";
      if (sklopA < sklopB) {
        return -1;
      }
      if (sklopA > sklopB) {
        return 1;
      }

      return 0;
    });

    const sklopi = new Set(
      pvResults
        .filter((val) => (sklop == null ? true : sklop === val.lastnost?.sklop))
        .filter((val) => val.lastnost?.sklop != null)
        .map((breedingValue) => breedingValue.lastnost?.sklop)
    );
    return Array.from(sklopi).map((sklop) => {
      return {
        sklop: sklop,
        values: pvResults.filter((val) => val.lastnost?.sklop === sklop),
      };
    });
  }, [
    breedingValueCodeQuery.result?.length,
    pv.result?.content?.length,
    values.sklop,
  ]);

  useEffect(() => {
    breedingValueCodeQuery
      .query({
        page: new ApiPage().page(0).size(1000),
        filter: filterLastnostiPvBySpecies(),
      })
      .then((result) => {
        setBreedingValueCodes(result.content ?? []);
      });
  }, []);

  useEffect(() => {
    if (values.zivalId != null) {
      plemenskaZivalQuery
        .query({
          filter: new ApiFilter().equal(
            "eksterniEnolicniIdentifikator",
            values.zivalId
          ),
        })
        .then((pzPage) => {
          const pz = pzPage.content?.[0];
          setPlemenskaZival(pz);
        });
    }
    setPlemenskaZival(null);
    setZival(null);
  }, [values.zivalId]);

  const getInitialAnimalKodaIzracun = (animalId) => {
    initialAnimalPv
      .query({
        page: new ApiPage().size(1),
        filter: new ApiFilter()
          .equal("plemenskaZival.id", animalId)
          .equal("kodaPopulacije", values.kodaPopulacije),
        sort: new ApiSort().sort("kodaIzracun", "DESC"),
      })
      .then((response) => {
        setInitialAnimalKodaIzracun(
          response?.content?.[0]?.kodaIzracun ?? null
        );
      });
  };

  // Logic to get zival from plemenskaZival
  useEffect(() => {
    if (plemenskaZival?.eksterniEnolicniIdentifikator != null) {
      zivalIdentifikatorQuery
        .query({
          filter: new ApiFilter()
            .equal("vrednost", plemenskaZival.eksterniEnolicniIdentifikator)
            .in("tip", ["USESNA_STEVILKA", "INTERBUL"]),
        })
        .then((result) => {
          if (result.content?.length > 0) {
            result.content.map((identifikator) => {
              zivalAggregateRead.read(identifikator.zivalId).then((zival) => {
                if (zival != null) {
                  setZival(zival);
                }
              });
            });
          }
        });
    }
  }, [plemenskaZival]);

  useEffect(() => {
    if (initialAnimalKodaIzracun === undefined) {
      return;
    }

    const koda = initialAnimalKodaIzracun ?? kodaIzracunQuery.result?.list?.[0];
    if (koda != null) {
      getField("verzijeKataloga").setValue([koda]);
    }
  }, [kodaIzracunQuery.result?.list, initialAnimalKodaIzracun]);

  useEffect(() => {
    if (wasInitialFetchTriggered) {
      return;
    }
    if (plemenskaZival != null && values.verzijeKataloga?.length > 0) {
      setWasInitialFetchTriggered(true);
      setInitialValues(values);
      searchBreedingValues(values);
    }
  }, [values.verzijeKataloga, values.zivalId]);

  useEffect(() => {
    if (kodaIzracunQuery.result?.list == null) {
      kodaIzracunQuery.query();
    }

    if (animalId == null) {
      // when animalID param is not provided
      // we can try to fetch the first animal from the list of filtered animals

      // filter only species that matter
      const filter = species.filter();

      plemenskaZivalQuery
        .query({ page: new ApiPage().size(1), filter })
        .then((result) => {
          if (result?.content?.length > 0) {
            const animal = result.content[0];
            getField("zivalId").setValue(animal.eksterniEnolicniIdentifikator);
            if (animal?.id != null) {
              getInitialAnimalKodaIzracun(animal.id);
            }
          }
        });
    }
  }, [animalId]);

  const latestVerzijaKataloga = useMemo(() => {
    return values.verzijeKataloga?.sort()?.reverse()?.[0];
  }, [values.verzijeKataloga?.length]);

  useEffect(() => {
    const pokkAnimalId = animalId ?? zival?.id;
    const lastnost = values.lastnost;
    if (
      pokkAnimalId != null &&
      latestVerzijaKataloga != null &&
      lastnost != null
    ) {
      dispatch(
        getRangPvForAnimal({
          animalId: pokkAnimalId,
          kodaPv: lastnost,
          kodaIzracun: latestVerzijaKataloga,
        })
      );
    }
  }, [animalId, zival?.id, latestVerzijaKataloga, values.lastnost]);

  useCallbackOnMatch(({ payload }) => {
    setRangForAnimal(payload?.[0]);
  }, getRangPvForAnimal.fulfilled);

  const CatalogTables = ({ showPrintButton = true }) => {
    return (
      <Conditional condition={values.zivalId != null}>
        <div className="content-wrapper">
          <Conditional condition={showPrintButton}>
            <div className="buttons is-right m-0">
              <Button icon="icon-file-pdf" onClick={() => window.print()} />
            </div>
          </Conditional>
          <div className="is-flex is-flex-direction-column is-gap-2">
            {breedingValuesLists.map((breedingValuesList) => {
              return (
                <CatalogTable
                  key={breedingValuesList.sklop}
                  data={breedingValuesList.values}
                  title={breedingValuesList.sklop}
                  breedingValueCodes={breedingValueCodes}
                  kodePopulacij={kodePopulacij}
                />
              );
            })}
            <Conditional condition={breedingValuesLists.length === 0}>
              <div className="has-text-centered">
                <span className="icon icon-circle-x"></span>
                {t("breedingValues.noData")}
              </div>
            </Conditional>
          </div>
        </div>
      </Conditional>
    );
  };

  return (
    <>
      <form onSubmit={submit} className="breeding-values-page">
        <Conditional condition={showHeader}>
          <div className="is-hidden-print">
            <PageHeader
              title={t("breedingValues.catalog")}
              subtitle={t("breedingValues.catalog.description")}
              backURL=".."
            />
          </div>
        </Conditional>
        <Conditional condition={animalId == null}>
          <Card className="is-hidden-print">
            <Conditional condition={values.zivalId == null}>
              <DataLayerSearchSelect
                ident={DLD.PLEMENSKA_ZIVAL}
                queryFields={["eksterniEnolicniIdentifikator"]}
                optionId="id"
                filterFunc={(options) => {
                  const seen = new Set();
                  return options.filter((item) => {
                    const id = item.eksterniEnolicniIdentifikator;
                    if (id == null || seen.has(id)) {
                      return false;
                    }
                    seen.add(id);
                    return true;
                  });
                }}
                onSelection={(zivalId) => {
                  searchBreedingValues({ ...values, zivalId: zivalId });
                }}
                filter={() => species.filter()}
                optionLabel={(animal) =>
                  [
                    animal.eksterniEnolicniIdentifikator == null
                      ? null
                      : `ID.: ${animal.eksterniEnolicniIdentifikator}`,
                    animal.datumRojstva == null
                      ? null
                      : `Dat. roj: ${toHumanReadableDate(animal.datumRojstva)}`,
                  ]
                    .filter(Boolean)
                    .join(", ")
                }
                optionValue="eksterniEnolicniIdentifikator"
                label={t("animal.animal")}
                formField={getField("zivalId")}
                labelType="floating"
              />
            </Conditional>
            <AnimalDetailsCard
              plemenskaZival={plemenskaZival}
              zival={zival}
              rang={
                values.lastnost != null &&
                values.lastnost === rangForAnimal?.kodaPv
                  ? rangForAnimal
                  : null
              }
              latestVerzijaKataloga={latestVerzijaKataloga}
              onDeselect={() => getField("zivalId").setValue(null)}
            />
          </Card>
        </Conditional>
        <Conditional condition={animalId != null && plemenskaZival != null}>
          <AnimalDetailsCard
            plemenskaZival={plemenskaZival}
            zival={zival}
            rang={
              values.lastnost != null &&
              values.lastnost === rangForAnimal?.kodaPv
                ? rangForAnimal
                : null
            }
            latestVerzijaKataloga={latestVerzijaKataloga}
          />
        </Conditional>
        <Card className="is-hidden-print">
          <div className="columns">
            <div className="column is-half">
              <div className="is-flex is-flex-direction-column is-gap-2">
                <div>
                  <SelectLookupTableItem
                    ident={LTD.SifPlemVredKodaPopulacije}
                    label={t("breedingValues.populationCode")}
                    optionLabel={(kodaPopulacije) =>
                      kodaPopulacije.sifra + " - " + kodaPopulacije.naziv?.sl
                    }
                    labelType="floating"
                    formField={getField("kodaPopulacije")}
                  />
                </div>
                <BreedingValuesTipPvCheckbox formField={getField("tipiPv")} />
              </div>
            </div>
            <div className="column is-half">
              <div className="is-flex is-flex-direction-column is-gap-2">
                <div className="columns">
                  <div className="column">
                    <Select
                      options={distinctSklopi}
                      label={t("breedingValues.section")}
                      labelType="floating"
                      formField={getField("sklop")}
                    />
                  </div>
                  <div className="column">
                    <DataLayerSearchSelect
                      ident={DLD.PLEMENSKA_VREDNOST_KODA}
                      queryFields={["sifra", "naziv.sl"]}
                      optionId="sifra"
                      labelType="floating"
                      optionLabel={(koda) =>
                        [koda.naziv?.sl, koda.sifra]
                          .filter(Boolean)
                          .join(" - ")
                          .toUpperCase()
                      }
                      optionValue="sifra"
                      selectionMode="single"
                      filter={() => {
                        const filter = filterLastnostiPvBySpecies();
                        if (values.sklop != null) {
                          filter.equal("sklop", values.sklop);
                        }
                        return filter;
                      }}
                      label={t("breedingValues.property")}
                      formField={getField("lastnost")}
                    />
                  </div>
                </div>
                <KodaIzracunSelect formField={getField("verzijeKataloga")} />

                <BreedingValuesMetodaObracunaCheckbox
                  filter={(m) => ["N", "I"].includes(m.sifra)}
                  formField={getField("metodeIzracuna")}
                />
              </div>
            </div>
          </div>
          <div className="is-flex is-justify-content-space-between">
            <Button
              isSubmit={true}
              isLoading={pv.isLoading}
              label={t("breedingValues.drawCatalog")}
              color="primary"
            />
            <Button
              label={t("common.reset")}
              onClick={() => {
                setValues(initialValues);
                submit(initialValues);
              }}
              isLoading={pv.isLoading}
            />
          </div>
        </Card>
        <Conditional condition={values.zivalId != null}>
          <Card>
            <CatalogTables />
          </Card>
        </Conditional>
      </form>
      <PrintContent orientation="landscape">
        <CatalogTables showPrintButton={false} />
      </PrintContent>
    </>
  );
};

const CatalogTable = ({ data, title, breedingValueCodes, kodePopulacij }) => {
  const { t } = useTranslation();
  return (
    <div className="catalog-table">
      <div>{title}</div>
      <Table
        rowId="id"
        data={data}
        columns={[
          {
            id: "kodaPv",
            name: t("breedingValues.columns.code"),
            isSortable: false,
            cellComponent: (breedingValue) => breedingValue.kodaPv ?? "-",
          },
          {
            id: "lastnost",
            name: t("breedingValues.columns.property"),
            isSortable: false,
            cellComponent: (row) => {
              if (row == null) {
                return "-";
              }
              const breedingValueCode = breedingValueCodes?.find(
                (breedingValueCode) => breedingValueCode.sifra === row.kodaPv
              );
              return breedingValueCode?.naziv?.sl ?? row.kodaPv;
            },
          },
          {
            id: "kodaPopulacije",
            name: t("breedingValues.columns.population"),
            isSortable: false,
            cellComponent: (breedingValue) => {
              const kodaPopulacije = (kodePopulacij ?? []).find(
                (kp) => kp.sifra === breedingValue.kodaPopulacije
              );
              return (
                <span
                  className="has-tooltip-top"
                  data-tooltip={kodaPopulacije?.naziv?.sl ?? undefined}>
                  {breedingValue.kodaPopulacije ?? "-"}
                </span>
              );
            },
          },
          {
            id: "metodaIzracuna",
            name: t("breedingValues.columns.calculationMethod"),
            isSortable: false,
            cellComponent: (breedingValue) =>
              breedingValue.metodaIzracuna ?? "-",
          },
          {
            id: "tipPlemenskeVrednosti",
            name: t("breedingValues.columns.breedingValueType"),
            isSortable: false,
            cellComponent: (breedingValue) =>
              breedingValue.tipPlemenskeVrednosti ?? "-",
          },
          {
            id: "kodaIzracun",
            name: t("breedingValues.columns.calculationCode"),
            isSortable: false,
            cellComponent: (breedingValue) => breedingValue.kodaIzracun ?? "-",
          },
          {
            id: "tocnostPv",
            name: t("breedingValues.columns.accuracy"),
            isSortable: false,
            cellComponent: (breedingValue) =>
              breedingValue.tocnostPv
                ? (breedingValue.tocnostPv * 100).toFixed(0)
                : "-",
          },
          {
            id: "steviloUporabljenihNEnot",
            name: t("breedingValues.columns.numberOfUnits"),
            isSortable: false,
            cellComponent: (breedingValue) =>
              breedingValue.steviloUporabljenihNEnot ?? "-",
          },
          {
            id: "effectiveDaughterContribution",
            name: t("breedingValues.columns.edc"),
            isSortable: false,
            cellComponent: (breedingValue) =>
              breedingValue.effectiveDaughterContribution ?? "-",
          },
          {
            id: "povprecjeUporabljenihPodatkov",
            name: t("breedingValues.columns.averageUsedData"),
            isSortable: false,
            cellComponent: (breedingValue) =>
              breedingValue.povprecjeUporabljenihPodatkov
                ? Math.round(
                    breedingValue.povprecjeUporabljenihPodatkov * 1000
                  ) / 1000
                : "-",
          },
          {
            id: "absolutnaPv",
            name: t("breedingValues.columns.absoluteValue"),
            isSortable: false,
            cellComponent: (breedingValue) =>
              breedingValue.absolutnaPv
                ? Math.round(breedingValue.absolutnaPv * 1000) / 1000
                : "-",
          },
          {
            id: "standardiziranaPv",
            name: t("breedingValues.columns.standardizedValue"),
            isSortable: false,
            cellComponent: (breedingValue) =>
              breedingValue.standardiziranaPv
                ? Math.round(breedingValue.standardiziranaPv * 10) / 10
                : "-",
          },
          {
            id: "uradnostVrednosti",
            name: t("breedingValues.columns.valueOfficiality"),
            isSortable: false,
            cellComponent: (breedingValue) => {
              if (breedingValue.uradnostVrednosti == null) {
                return "-";
              }
              return breedingValue.uradnostVrednosti ? (
                <span className="icon icon-check is-success is-small"></span>
              ) : (
                <span className="icon icon-xmark is-danger is-small"></span>
              );
            },
          },
          {
            id: "rangAbsolutno",
            name: t("breedingValues.columns.absoluteRang"),
            isSortable: false,
            cellComponent: (breedingValue) =>
              breedingValue.rangAbsolutno ?? "-",
          },
          {
            id: "rangProcentno",
            name: t("breedingValues.columns.rangPercent"),
            isSortable: false,
            cellComponent: (breedingValue) =>
              breedingValue.rangProcentno
                ? Math.round(breedingValue.rangProcentno * 1000) / 1000
                : "-",
          },
          {
            id: "minValue",
            name: 64,
            isSortable: false,
            cellComponent: (breedingValue) => {
              const breedingValueCode = (breedingValueCodes ?? []).find(
                (code) => {
                  return breedingValue.kodaPv === code.sifra;
                }
              );
              return breedingValueCode?.labela64 ?? "-";
            },
          },
          {
            id: "graph",
            name: t("breedingValues.columns.graph"),
            isSortable: false,
            cellComponent: (breedingValue) => {
              const breedingValueCode = (breedingValueCodes ?? []).find(
                (code) => {
                  return breedingValue.kodaPv === code.sifra;
                }
              );
              return (
                <div className="is-flex is-justify-content-center">
                  <OptimumGraph
                    pv={breedingValue.standardiziranaPv}
                    opt={breedingValueCode?.optimum}
                  />
                </div>
              );
            },
          },
          {
            id: "maxValue",
            name: 136,
            isSortable: false,
            cellComponent: (breedingValue) => {
              const breedingValueCode = (breedingValueCodes ?? []).find(
                (code) => {
                  return breedingValue.kodaPv === code.sifra;
                }
              );
              return breedingValueCode?.labela136 ?? "-";
            },
          },
        ]}
      />
    </div>
  );
};

const OptimumGraph = ({ pv, opt }) => {
  let pvx = pv;
  if (pv == null) {
    pvx = 100;
  } else if (pv < 64) {
    pvx = 64;
  } else if (pv > 136) {
    pvx = 136;
  }
  if (pvx == null) {
    return "-";
  }

  if (opt != null) {
    pvx = 136 - Math.abs((72 / (36 + Math.abs(opt - 100))) * (pvx - opt));
  }

  const barSize = (pvx - 64) / 72 - 0.5;

  const barLeft = barSize > 0 ? 50 : 50 - Math.abs(barSize * 100);

  const color = barSize > 0 ? "black" : "danger";

  return (
    <div className="optimum-graph">
      <span
        className={`optimum-bar has-background-${color}`}
        style={{
          width: `${Math.abs(barSize * 100)}%`,
          height: "100%",
          left: `${barLeft}%`,
        }}></span>
    </div>
  );
};

const AnimalDetailsCard = ({
  plemenskaZival,
  zival,
  onDeselect,
  rang,
  latestVerzijaKataloga,
}) => {
  const { t } = useTranslation();

  if (plemenskaZival == null) {
    return;
  }

  return (
    <div className="box fixed-grid">
      <Conditional condition={onDeselect != null}>
        <div className="is-flex is-flex-direction-row is-justify-content-space-between mb-4">
          <div className="is-size-5">{t("breedingValues.selectedAnimal")}</div>
          <span
            className="icon icon-circle-x is-clickable is-size-2 is-danger"
            onClick={onDeselect}></span>
        </div>
      </Conditional>
      <div className="grid">
        <span>{t("animal.animal")}</span>
        <span className="has-text-weight-bold">
          {plemenskaZival.eksterniEnolicniIdentifikator}
        </span>
        <span>{t("breed")}</span>
        <span className="has-text-weight-bold">
          {plemenskaZival.pasmaSifra}
        </span>
        <span>{t("inbreedingCoefficient")}</span>
        <span className="has-text-weight-bold">
          {zival?.koefInbriding ?? "/"}
        </span>
        <span>{t("birthDate")}</span>
        <span className="has-text-weight-bold">
          {plemenskaZival.datumRojstva == null
            ? "/"
            : toHumanReadableDate(plemenskaZival.datumRojstva)}
        </span>
        <span>{`${t("breedingValues.animalRang")} ${rang?.kodaIzracun == null ? `(${latestVerzijaKataloga ?? ""})` : `(${rang.kodaIzracun})`}`}</span>
        <span className="has-text-weight-bold">
          <Conditional condition={rang == null || rang.rangAbsolutno == null}>
            /
          </Conditional>
          <Conditional condition={rang != null && rang.rangAbsolutno != null}>
            {`${rang?.rangAbsolutno}/${rang?.rangAbsolutnoMax} (${rang?.rangProcentno?.toFixed(2)}%)`}
          </Conditional>
        </span>
      </div>
    </div>
  );
};

export default BreedingValuesCatalogPage;
